import React, { useState } from 'react';
import VideoUploadButton from '../../../components/ui/VideoUploadButton';
import Icon from '../../../components/AppIcon';

const LevelFourSection = ({ data, onChange, errors, songId, talentId }) => {
  const [uploadedVideos, setUploadedVideos] = useState({
    backstory: [],
    arc: [],
    physicality: [],
    facialExpressions: [],
    connection: [],
    exercises: []
  });
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const handleFieldChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const handleExerciseChange = (exerciseKey, value) => {
    onChange({
      ...data,
      exercises: {
        ...data?.exercises,
        [exerciseKey]: value
      }
    });
  };

  const handleVideoUploadSuccess = (uploadData, subsection) => {
    setUploadedVideos(prev => ({
      ...prev,
      [subsection]: [...prev?.[subsection], uploadData]
    }));
    setUploadSuccess(`Video uploaded successfully for ${subsection}: ${uploadData?.fileName}`);
    setUploadError('');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setUploadSuccess('');
    }, 3000);
  };

  const handleVideoUploadError = (error) => {
    setUploadError(error);
    setUploadSuccess('');
  };

  const mainFields = [
    { key: 'backstory', title: 'Backstory', placeholder: 'Describe the character\'s backstory and motivations...' },
    { key: 'arc', title: 'Arc', placeholder: 'Describe the character arc through this song...' },
    { key: 'physicality', title: 'Physicality', placeholder: 'Describe the physical performance aspects...' },
    { key: 'facialExpressions', title: 'Facial Expressions', placeholder: 'Describe facial expressions and emotional delivery...' },
    { key: 'connection', title: 'Connection', placeholder: 'Describe how to connect with the audience...' }
  ];

  const exercises = [
    { key: 'exercise1', title: 'Exercise 1', placeholder: 'First exercise for this song...' },
    { key: 'exercise2', title: 'Exercise 2', placeholder: 'Second exercise for this song...' },
    { key: 'exercise3', title: 'Exercise 3', placeholder: 'Third exercise for this song...' }
  ];

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
      {/* Section Header */}
      <h2 className="text-2xl font-heading font-bold text-white mb-6">Level 4: Cast Ready</h2>
      
      {/* Upload Messages */}
      {uploadError && (
        <div className="mb-4 bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-3">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-red-800" />
            <p className="text-red-800 text-sm">{uploadError}</p>
          </div>
        </div>
      )}

      {uploadSuccess && (
        <div className="mb-4 bg-green-50 bg-opacity-90 border border-green-200 rounded-xl p-3">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-green-800" />
            <p className="text-green-800 text-sm">{uploadSuccess}</p>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Main Fields */}
        {mainFields?.map((field) => (
          <div key={field?.key} className="border border-white border-opacity-20 rounded-xl p-4">
            {/* Field Header with Video Upload */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h3 className="text-lg font-semibold text-white">{field?.title}</h3>
              
              <VideoUploadButton
                level="level4"
                subsection={field?.key}
                songId={songId}
                talentId={talentId}
                onUploadSuccess={(uploadData) => handleVideoUploadSuccess(uploadData, field?.key)}
                onUploadError={handleVideoUploadError}
                size="sm"
                className="shrink-0"
              />
            </div>

            {/* Uploaded Videos for this subsection */}
            {uploadedVideos?.[field?.key]?.length > 0 && (
              <div className="mb-4 bg-blue-50 bg-opacity-90 border border-blue-200 rounded-xl p-3">
                <h4 className="text-blue-800 font-medium mb-2 text-sm">
                  {field?.title} Videos ({uploadedVideos?.[field?.key]?.length})
                </h4>
                <div className="space-y-1">
                  {uploadedVideos?.[field?.key]?.map((video, index) => (
                    <div key={index} className="flex items-center justify-between text-xs text-blue-700 bg-white bg-opacity-50 rounded-lg p-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="Video" size={12} />
                        <span>{video?.fileName}</span>
                      </div>
                      <span className="text-xs">
                        {(video?.fileSize / (1024 * 1024))?.toFixed(1)} MB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <textarea
              value={data?.[field?.key] || ''}
              onChange={(e) => handleFieldChange(field?.key, e?.target?.value)}
              placeholder={field?.placeholder}
              rows={4}
              className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
            />
          </div>
        ))}

        {/* Exercises Section */}
        <div className="border border-white border-opacity-20 rounded-xl p-4">
          {/* Exercises Header with Video Upload */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h3 className="text-lg font-semibold text-white">Performance Exercises</h3>
            
            <VideoUploadButton
              level="level4"
              subsection="exercises"
              songId={songId}
              talentId={talentId}
              onUploadSuccess={(uploadData) => handleVideoUploadSuccess(uploadData, 'exercises')}
              onUploadError={handleVideoUploadError}
              size="sm"
              className="shrink-0"
            />
          </div>

          {/* Uploaded Videos for exercises */}
          {uploadedVideos?.exercises?.length > 0 && (
            <div className="mb-4 bg-blue-50 bg-opacity-90 border border-blue-200 rounded-xl p-3">
              <h4 className="text-blue-800 font-medium mb-2 text-sm">
                Exercise Videos ({uploadedVideos?.exercises?.length})
              </h4>
              <div className="space-y-1">
                {uploadedVideos?.exercises?.map((video, index) => (
                  <div key={index} className="flex items-center justify-between text-xs text-blue-700 bg-white bg-opacity-50 rounded-lg p-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="Video" size={12} />
                      <span>{video?.fileName}</span>
                    </div>
                    <span className="text-xs">
                      {(video?.fileSize / (1024 * 1024))?.toFixed(1)} MB
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {exercises?.map((exercise) => (
              <div key={exercise?.key}>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  {exercise?.title}
                </label>
                <textarea
                  value={data?.exercises?.[exercise?.key] || ''}
                  onChange={(e) => handleExerciseChange(exercise?.key, e?.target?.value)}
                  placeholder={exercise?.placeholder}
                  rows={3}
                  className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelFourSection;